/**
 * Wire types of the guest trash (W6, plan squishy-coalescing-sutton §1.2 WP8).
 *
 * Everything here is read defensively: the FE ships BEFORE the backend hop
 * that answers these shapes (deploy order consumer → cron → BE W6-dark → FE →
 * BE W6-on), so an old backend answers the delete doors with an empty body and
 * `/trash/count` with a 404 — both are legal inputs for every consumer below.
 */

/** Answer of both delete doors once the backend knows the trash. */
export type DeleteGuestsResult = {
  mode: "TRASH" | "PERMANENT";
  count: number;
  /** Set in TRASH mode — the handle the 15 s undo restores by. */
  trashBatchId?: string | null;
  /** ISO instant; set in TRASH mode. */
  purgeAt?: string | null;
};

/** `GuestDeleteImpactDTO.trash` — absent on a backend without the trash. */
export type GuestDeleteImpactTrash = {
  enabled: boolean;
  retentionDays: number;
  /** ISO instant the rows would be purged at, when `enabled`. */
  purgeAt: string | null;
};

/**
 * `GET …/trash/count` — `enabled` says whether the trash is ON for the event
 * (audit-B H1; the sidebar shows the Papierkorb on it). An older backend
 * answers `{count}` only ⇒ `enabled: null` and the learned flag decides.
 */
export type GuestTrashCountResult = {
  count: number;
  enabled: boolean | null;
};

/**
 * A ROW's status (`GuestTrash.java`): TRASHED | RESTORING | PURGING | PURGED.
 * `STAGING` is a BATCH status and never appears on a row; an unknown value
 * still renders (the chip falls back to the raw status).
 */
export type GuestTrashStatus = "TRASHED" | "RESTORING" | "PURGING" | "PURGED" | string;

/** A row's actions are offered only while it sits still in the trash. */
export const GUEST_TRASH_BUSY_STATUSES: ReadonlySet<string> = new Set(["RESTORING", "PURGING"]);

export type GuestTrashItem = {
  guestId: string;
  batchId: string | null;
  trashedAt: string | null;
  purgeAt: string | null;
  status: GuestTrashStatus;
  displayName: string | null;
  email: string | null;
  mainGuestId: string | null;
  companionCount: number;
};

export type GuestTrashPage = {
  items: GuestTrashItem[];
  total: number;
};

export type GuestTrashRestoreRequest = {
  guestIds?: string[];
  batchId?: string;
  includeCompanions?: boolean;
  dryRun?: boolean;
};

/**
 * `usernameRegenerated` ⚑10 · `speakerLinkDropped` · `possibleDuplicate` ⚑18 ·
 * `fieldDropped` · `optionCapacityDropped` (`GuestTrashRestoreService`) and
 * B5's placement codes `seatNotPlaced` · `sessionNotPlaced` ·
 * `appointmentNotPlaced` · `waitlistNotPlaced` · `requestNotPlaced` ·
 * `speakerNotRelinked` (`GuestTrashChildrenRestoreService`) — warn, never block.
 */
export type GuestTrashRestoreWarning = {
  guestId: string;
  code: string;
  /**
   * The backend's `Warning.details` map (`Map<String, Object>`, omitted when
   * empty): `{username}` / `{field, reason}` / `{furnitureId, seatIndex, reason}`
   * / `{appointmentId, reason}` … Only `reason` is read (`gone | full | taken |
   * invalid | failed`, else "other"); nothing else is shown raw.
   */
  details?: Record<string, unknown> | null;
};

/** `reason`: notClaimed | live | eventGone | notInTrash | busy | failed (purge) / the restore's own set. */
export type GuestTrashSkipped = {
  guestId: string;
  reason: string;
};

export type GuestTrashRestoreResult = {
  restored: number;
  skipped: GuestTrashSkipped[];
  warnings: GuestTrashRestoreWarning[];
};

export type GuestTrashPurgeRequest = {
  guestIds?: string[];
  batchId?: string;
};

/**
 * `DELETE …/trash` — 200 `{purged, skipped, receiptIds}` for one unit, or
 * 202 `{purged: 0, queued, skipped}` when the selection spans more than one
 * unit and the sweeper takes it from here.
 */
export type GuestTrashPurgeResult = {
  purged: number;
  /** Rows marked due for the sweeper (the 202 answer); 0 on a 200. */
  queued: number;
  skipped: GuestTrashSkipped[];
  receiptIds: string[];
};

/** `{guestId, reason}` entries only; anything else the backend might send is dropped, not crashed on. */
export function readSkipped(value: unknown): GuestTrashSkipped[] {
  if (!Array.isArray(value)) return [];
  const out: GuestTrashSkipped[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object") continue;
    const { guestId, reason } = entry as { guestId?: unknown; reason?: unknown };
    if (typeof guestId !== "string") continue;
    out.push({ guestId, reason: typeof reason === "string" ? reason : "unknown" });
  }
  return out;
}

/** The delete door answered in TRASH mode with a batch to undo by. */
export function isTrashDeleteResult(
  value: unknown,
): value is DeleteGuestsResult & { trashBatchId: string } {
  return (
    !!value &&
    typeof value === "object" &&
    (value as { mode?: unknown }).mode === "TRASH" &&
    typeof (value as { trashBatchId?: unknown }).trashBatchId === "string" &&
    (value as { trashBatchId: string }).trashBatchId.length > 0
  );
}

/** A loosely-typed delete answer → a typed result, or null for an empty/old-backend body. */
export function readDeleteGuestsResult(value: unknown): DeleteGuestsResult | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Partial<DeleteGuestsResult>;
  if (v.mode !== "TRASH" && v.mode !== "PERMANENT") return null;
  return {
    mode: v.mode,
    count: typeof v.count === "number" ? v.count : 0,
    trashBatchId: typeof v.trashBatchId === "string" ? v.trashBatchId : null,
    purgeAt: typeof v.purgeAt === "string" ? v.purgeAt : null,
  };
}
